import { SelectOptionType } from 'dashboard/components/JsonSchemaSelect/SelectOption';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import {
  WorkflowModule,
  WorkflowModulePayload,
  WorkflowModulePropagateMeta,
} from 'dashboard/models/workflow-module';
import { dashboardApi, LIST_TAG_ID } from 'dashboard/redux/api/api';
import { ApiOptions } from 'lib/clients/ApiClient';
import {
  ApiEndpoint,
  ApiListResponse,
  ApiModel,
  ApiPayload,
  ApiResponse,
  PaginationParams,
} from 'lib/interfaces/api';

export interface WorkflowModulePaginationParams extends PaginationParams {
  'workflow-options': SelectOptionType[];
}

export const workflowModuleApi = dashboardApi.injectEndpoints({
  endpoints: (builder) => ({
    listWorkflowModules: builder.query<
      {
        workflowModules: WorkflowModule[];
        workflowBlocks: WorkflowBlock[];
        paginationParams: PaginationParams | undefined;
        workflowOptions: WorkflowModulePaginationParams['workflow-options'] | undefined;
      },
      ApiOptions
    >({
      query: (options) =>
        SessionClientDashboard.listResource<WorkflowModule, PaginationParams>(
          ApiEndpoint.WorkflowModule,
          options,
        ),
      transformResponse: (
        response: ApiListResponse<WorkflowModule, WorkflowModulePaginationParams>,
      ) => ({
        workflowModules: response.data || [],
        workflowBlocks:
          response.included?.filter(
            (include): include is WorkflowBlock => include.type === ApiModel.WorkflowBlock,
          ) || [],
        paginationParams: response.meta,
        workflowOptions: response.meta?.['workflow-options'],
      }),
      providesTags: [{ type: ApiModel.WorkflowModule, id: LIST_TAG_ID }],
    }),
    archiveWorkflowModule: builder.mutation<WorkflowModule, WorkflowModule>({
      query: async (workflowModule) =>
        await SessionClientDashboard.deleteResource<WorkflowModule>(
          ApiEndpoint.WorkflowModule,
          workflowModule.id,
        ),
      invalidatesTags: (_result, _error, { id }) => {
        return [
          { type: ApiModel.WorkflowModule, id: LIST_TAG_ID },
          { type: ApiModel.WorkflowModule, id },
        ];
      },
    }),
    activateWorkflowModule: builder.mutation<WorkflowModule, WorkflowModule>({
      query: async (workflowModule) =>
        await SessionClientDashboard.postResourceAction<WorkflowModule>(workflowModule, 'activate'),
      invalidatesTags: (_result, _error, { id }) => {
        return [
          { type: ApiModel.WorkflowModule, id: LIST_TAG_ID },
          { type: ApiModel.WorkflowModule, id },
        ];
      },
    }),
    deactivateWorkflowModule: builder.mutation<WorkflowModule, WorkflowModule>({
      query: async (workflowModule) =>
        await SessionClientDashboard.postResourceAction<WorkflowModule>(
          workflowModule,
          'deactivate',
        ),
      invalidatesTags: (_result, _error, { id }) => {
        return [
          { type: ApiModel.WorkflowModule, id: LIST_TAG_ID },
          { type: ApiModel.WorkflowModule, id },
        ];
      },
    }),
    getWorkflowModule: builder.query<ApiResponse<WorkflowModule>, string>({
      query: async (id) =>
        SessionClientDashboard.getResource<WorkflowModule>(ApiEndpoint.WorkflowModule, id),
      providesTags: (_result, _error, id) => {
        return [{ type: ApiModel.WorkflowModule, id }];
      },
    }),
    updateWorkflowModule: builder.mutation<WorkflowModule, WorkflowModule>({
      query: async (workflowModule) =>
        await SessionClientDashboard.updateResource<WorkflowModule>({
          type: ApiModel.WorkflowModule,
          id: workflowModule.id,
          attributes: {
            name: workflowModule.attributes.name,
            description: workflowModule.attributes.description,
          },
        }),
      invalidatesTags: (_result, _error, { id }) => [{ type: ApiModel.WorkflowModule, id }],
    }),
    createWorkflowModule: builder.mutation<ApiResponse<WorkflowModule>, WorkflowModulePayload>({
      query: async (workflowModulePayload) =>
        await SessionClientDashboard.postResourceCreate<WorkflowModule>(workflowModulePayload),
      invalidatesTags: (_result, _error) => [{ type: ApiModel.WorkflowModule, id: LIST_TAG_ID }],
    }),
    propagateWorkflowModule: builder.mutation<
      ApiResponse<WorkflowModule, WorkflowModulePropagateMeta>,
      { workflowModuleId: string; workflowIds: string[] }
    >({
      query: async ({ workflowModuleId, workflowIds }) =>
        await SessionClientDashboard.postResourceAction<
          WorkflowModule,
          ApiPayload<{}, ApiModel.WorkflowModule>,
          WorkflowModulePropagateMeta
        >({ type: ApiModel.WorkflowModule, id: workflowModuleId }, 'propagate', {
          meta: {
            'workflow-ids': workflowIds,
          },
        }),
      invalidatesTags: (_result, _error, { workflowModuleId }) => {
        return [
          { type: ApiModel.WorkflowModule, id: LIST_TAG_ID },
          { type: ApiModel.WorkflowModule, id: workflowModuleId },
        ];
      },
    }),
  }),
});

// Hooks are autogenerated based on the defined endpoints
export const {
  useListWorkflowModulesQuery,
  useGetWorkflowModuleQuery,
  useArchiveWorkflowModuleMutation,
  useActivateWorkflowModuleMutation,
  useDeactivateWorkflowModuleMutation,
  useUpdateWorkflowModuleMutation,
  useCreateWorkflowModuleMutation,
  usePropagateWorkflowModuleMutation,
  endpoints: workflowModuleEndpoints,
} = workflowModuleApi;
